/**
 * Possible worlds (AI-01, AI-02). A world fills in every card the AI cannot see — opponents' hands,
 * a face-down middle, the bones and the discard pile — consistently with what it has seen:
 *   - its own hand and the visible middle are fixed;
 *   - opponents hold the cards it saw them take (or, with inference on, probably still hold);
 *   - its own blind discards stay in the discard pile until a reshuffle;
 *   - every other card is dealt uniformly at random into the remaining slots.
 * Sampling uses the AI's own simulation RNG and never reads the live game.
 */
import { makeDeck, type Card } from "../cards.ts";
import type { TurnView } from "../whiskey.ts";
import type { Rng } from "../rng.ts";
import { cardKey, type CardMemory } from "./memory.ts";
import type { SimState } from "./sim.ts";

const DECK: Card[] = makeDeck();
const BY_KEY: Card[] = (() => { const a: Card[] = []; for (const c of DECK) a[cardKey(c)] = c; return a; })();

export interface WorldView {
  seat: number;
  hand: Card[];
  middle: (Card | null)[];
  middleRevealed: boolean;
  /** Face up but taking all five isn't open: this seat turned it over this turn. */
  justRevealed: boolean;
  stockCount: number;
  discardCount: number;
  knockerSeat: number;
  dealerSeat: number;
  finalTurnsLeft: number;
  turnsTaken: number[];
  consecutiveKeeps: number;
  actions: number;
}

export function worldViewOf(v: TurnView): WorldView {
  return {
    seat: v.seat, hand: v.hand, middle: v.middle, middleRevealed: v.middleRevealed,
    justRevealed: v.middleRevealed && v.legal.length > 0 && !v.legal.includes("TAKE_MIDDLE"),
    stockCount: v.stockCount, discardCount: v.discardCount,
    knockerSeat: v.table.knockerSeat, dealerSeat: v.table.dealerSeat, finalTurnsLeft: v.table.finalTurnsLeft,
    turnsTaken: v.table.turnsTaken, consecutiveKeeps: v.table.consecutiveKeeps, actions: v.table.actionsThisHand,
  };
}

export function sampleWorld(v: WorldView, mem: CardMemory, rng: Rng, useInference: boolean): SimState {
  const me = mem.me;
  const fixed = new Set<number>();
  for (const c of v.hand) fixed.add(cardKey(c));
  for (const c of v.middle) if (c) fixed.add(cardKey(c));

  const hands: Card[][] = [[], [], [], [], []];
  hands[me] = v.hand.map((c) => ({ ...c }));
  const toDiscard: number[] = [], toStock: number[] = [];
  for (const k of mem.ownDiscards) if (!fixed.has(k)) { toDiscard.push(k); fixed.add(k); }
  for (let s = 0; s < 5; s++) {
    if (s === me) continue;
    for (const [k, h] of mem.held[s]) {
      if (fixed.has(k)) continue;
      const p = useInference ? h.p : h.p >= 1 ? 1 : 0;
      if (p >= 1 || (p > 0 && rng.next() < p)) {
        if (hands[s].length < 5) { hands[s].push(BY_KEY[k]); fixed.add(k); }
      } else if (useInference && h.p < 1) {
        // it was thrown away: still in the discard pile, unless the pile has since become the bones
        (h.reshuffledSince ? toStock : toDiscard).push(k); fixed.add(k);
      }
    }
  }
  const pool: Card[] = [];
  for (let k = 0; k < 52; k++) if (!fixed.has(k)) pool.push(BY_KEY[k]);
  rng.shuffle(pool);

  for (let s = 0; s < 5; s++) if (s !== me) while (hands[s].length < 5) hands[s].push(pool.pop()!);
  const middle: Card[] = v.middleRevealed ? v.middle.map((c) => ({ ...c! })) : [pool.pop()!, pool.pop()!, pool.pop()!, pool.pop()!, pool.pop()!];

  // Discard pile: known discards first; any that don't fit (counts disagree) go to the bones.
  const discard: Card[] = [];
  for (const k of toDiscard) {
    if (discard.length < v.discardCount) discard.push(BY_KEY[k]);
    else toStock.push(k);
  }
  while (discard.length < v.discardCount && pool.length) discard.push(pool.pop()!);
  const stockCards = [...toStock.map((k) => BY_KEY[k]), ...pool];
  rng.shuffle(stockCards);
  // keep the bones exactly the public size (extra cards would only come from inconsistent counts)
  const stock = stockCards.slice(0, v.stockCount);
  for (const c of stockCards.slice(v.stockCount)) discard.push(c);

  return {
    hands, middle, middleRevealed: v.middleRevealed, justRevealed: v.justRevealed, stock, discard,
    seat: v.seat, dealerSeat: v.dealerSeat, knockerSeat: v.knockerSeat, finalTurnsLeft: v.finalTurnsLeft,
    turnsTaken: v.turnsTaken.slice(), consecutiveKeeps: v.consecutiveKeeps, actions: v.actions, done: false,
  };
}
